import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  List, 
  ListOrdered, 
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Undo,
  Redo,
  RemoveFormatting
} from 'lucide-react';
import { Button } from './ui/button';
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import config from '../config';

const API = `${config.BACKEND_URL}/api`;

// Uploads an image and returns its absolute, directly-renderable URL.
const uploadInlineImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  // Let axios set the multipart boundary automatically for FormData.
  const res = await axios.post(`${API}/inline-images/upload`, formData);
  return `${config.BACKEND_URL}${res.data.url}`;
};

const MenuBar = ({ editor }) => {
  const imageInputRef = useRef(null);

  if (!editor) {
    return null;
  }

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await uploadInlineImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      toast.error('Failed to upload image');
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-muted' : ''}`}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-muted' : ''}`}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('strike') ? 'bg-muted' : ''}`}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 1 }) ? 'bg-muted' : ''}`}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`h-8 w-8 p-0 ${editor.isActive('heading', { level: 2 }) ? 'bg-muted' : ''}`}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-muted' : ''}`}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-muted' : ''}`}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          const url = window.prompt('Enter URL');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-muted' : ''}`}
        title="Add Link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => imageInputRef.current?.click()}
        className="h-8 w-8 p-0"
        title="Insert Image"
        data-testid="rte-insert-image-btn"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImagePick}
        data-testid="rte-image-input"
      />
      
      <div className="w-px h-6 bg-border mx-1 self-center" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        className="h-8 w-8 p-0"
        title="Clear Formatting"
      >
        <RemoveFormatting className="h-4 w-4" />
      </Button>
      
      <div className="flex-1" />
      
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="h-8 w-8 p-0"
        title="Undo"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="h-8 w-8 p-0"
        title="Redo"
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter description...', 
  maxLength = 5000,
  className = '',
  'data-testid': testId
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable built-in link to use our custom Link extension
        link: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editorProps: {
      handleDrop(view, event, slice, moved) {
        // Let TipTap handle internal node moves
        if (moved) return false;
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
        if (images.length === 0) return false;
        event.preventDefault();
        const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
        const dropPos = coords ? coords.pos : view.state.selection.from;
        images.forEach((file) => {
          uploadInlineImage(file)
            .then((url) => {
              const node = view.state.schema.nodes.image.create({ src: url });
              view.dispatch(view.state.tr.insert(dropPos, node));
            })
            .catch(() => toast.error('Failed to upload image'));
        });
        return true;
      },
      handlePaste(view, event) {
        const files = event.clipboardData?.files;
        if (!files || files.length === 0) return false;
        const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
        if (images.length === 0) return false;
        event.preventDefault();
        const pastePos = view.state.selection.from;
        images.forEach((file) => {
          uploadInlineImage(file)
            .then((url) => {
              const node = view.state.schema.nodes.image.create({ src: url });
              view.dispatch(view.state.tr.insert(pastePos, node));
            })
            .catch(() => toast.error('Failed to upload image'));
        });
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Get text length for character counting
  const getTextLength = () => {
    if (!editor) return 0;
    return editor.state.doc.textContent.length;
  };

  const textLength = getTextLength();

  return (
    <div className={`rich-text-editor border rounded-md ${className}`} data-testid={testId}>
      <MenuBar editor={editor} />
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-3 min-h-[150px] focus:outline-none"
      />
      {maxLength && (
        <div className="px-3 pb-2">
          <p className={`text-xs text-right ${textLength > maxLength ? 'text-destructive' : 'text-muted-foreground'}`}>
            {textLength}/{maxLength} characters
          </p>
        </div>
      )}
      <style>{`
        .rich-text-editor .ProseMirror {
          min-height: 120px;
          outline: none;
        }
        .rich-text-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: hsl(var(--muted-foreground));
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .rich-text-editor .ProseMirror h1 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror h2 {
          font-size: 1.25em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror ul,
        .rich-text-editor .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        .rich-text-editor .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 6px;
          margin: 0.5em 0;
        }
        .rich-text-editor .ProseMirror img.ProseMirror-selectednode {
          outline: 2px solid hsl(var(--primary));
        }
      `}</style>
    </div>
  );
};

// Read-only display component for rich text content
export const RichTextDisplay = ({ content, className = '' }) => {
  if (!content) return null;
  
  return (
    <div 
      className={`rich-text-display prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: content }}
      style={{
        fontSize: '14px',
        lineHeight: '1.6'
      }}
    />
  );
};
